/**
 * @module components/layout/Footer
 * @description Site footer: brand mark, social links, support email,
 * legal links, recognition/directory badges, and the Stripe Climate widget.
 *
 * @extracted-from client/src/App.js (lines ~2812-2964)
 */

import React from 'react';
import { Mail } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="relative z-10 mt-12 border-t border-white/[0.06] bg-black/25 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          <div className="md:col-span-5 flex flex-col items-center md:items-start text-center md:text-left">
            <span className="font-display text-2xl md:text-3xl font-bold text-gradient-blue tracking-tight">
              CriticAI
            </span>
            <p className="mt-3 text-sm md:text-base text-critic-text-secondary font-normal leading-relaxed max-w-md">
              Analyze Your Music Tracks and Book Manuscripts
            </p>
            <div className="flex items-center justify-center md:justify-start gap-3 mt-6">
              <a
                href="https://instagram.com/criticaidotca"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-xl bg-gradient-to-r from-apple-purple to-apple-pink flex items-center justify-center text-white hover:scale-105 transition-transform duration-200 shadow-lg shadow-black/30"
                aria-label="CriticAI on Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://linkedin.com/company/criticai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-xl bg-gradient-to-r from-apple-blue to-apple-indigo flex items-center justify-center text-white hover:scale-105 transition-transform duration-200 shadow-lg shadow-black/30"
                aria-label="CriticAI on LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="md:col-span-4 flex flex-col items-center md:items-start md:border-l border-white/[0.06] md:pl-10">
            <p className="critic-eyebrow mb-4">Support</p>
            <a
              href="mailto:hello@criticai.ca"
              className="critic-footer-link inline-flex items-center gap-2"
            >
              <Mail className="w-4 h-4 shrink-0 opacity-80" aria-hidden />
              hello@criticai.ca
            </a>
          </div>

          <div className="md:col-span-3 flex flex-col items-center md:items-start">
            <p className="critic-eyebrow mb-4">Legal</p>
            <nav className="flex flex-col gap-2 text-center md:text-left" aria-label="Legal">
              <a href="/privacy-policy.html" className="critic-footer-link">
                Privacy Policy
              </a>
              <a href="/terms-of-service.html" className="critic-footer-link">
                Terms of Service
              </a>
            </nav>
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-white/[0.06]">
          <p className="critic-eyebrow text-center mb-8">Recognition &amp; badges</p>
          <div className="flex justify-center mb-6 px-4">
            <a
              href="https://www.producthunt.com/products/criticai?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-criticai"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:scale-[1.02]"
            >
              <img
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1001146&theme=light&t=1754850638651"
                alt="CriticAI on Product Hunt"
                className="w-auto h-auto max-w-full"
                style={{ maxWidth: '250px', height: '54px' }}
                width="250"
                height="54"
              />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 justify-items-center max-w-2xl mx-auto px-4 mb-8">
            <a
              href="https://launchigniter.com/product/criticai?ref=badge-criticai"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:scale-[1.02] flex justify-center w-full"
            >
              <img
                src="https://launchigniter.com/api/badge/criticai?theme=dark"
                alt="Featured on LaunchIgniter"
                className="w-auto h-auto max-w-full"
                style={{ maxWidth: '212px', height: '55px' }}
                width="212"
                height="55"
              />
            </a>
            <a
              href="https://startupfa.me/s/criticai?utm_source=criticai.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:scale-[1.02] flex justify-center w-full"
            >
              <img
                src="https://startupfa.me/badges/featured-badge.webp"
                alt="Featured on Startup Fame"
                className="w-auto h-auto max-w-full"
                style={{ maxWidth: '171px', height: '54px' }}
                width="171"
                height="54"
              />
            </a>
            <a
              href="https://www.uneed.best/tool/criticai"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:scale-[1.02] flex justify-center w-full"
            >
              <img
                src="https://www.uneed.best/EMBED1A.png"
                alt="Featured on Uneed"
                className="w-auto h-auto max-w-full"
                style={{ height: '54px', width: 'auto' }}
              />
            </a>
            <a
              href="https://turbo0.com/item/criticai"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:scale-[1.02] flex justify-center w-full"
            >
              <img
                src="https://img.turbo0.com/badge-listed-dark.svg"
                alt="Listed on Turbo0"
                className="w-auto h-auto max-w-full"
                style={{ height: '54px', width: 'auto' }}
              />
            </a>
          </div>
          <div className="flex justify-center px-4">
            <iframe
              width="380"
              height="82"
              style={{ border: 0, borderRadius: '8px', maxWidth: '100%' }}
              src="https://climate.stripe.com/badge/rgH4j7?theme=dark&size=large&locale=en-US"
              title="Stripe Climate contribution"
            />
          </div>
        </div>

        <p className="text-center text-xs text-critic-text-muted font-normal mt-12 tracking-wide">
          © 2026 CriticAI, Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
